import React, { useState, useMemo, useRef, useImperativeHandle, forwardRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import MarkerClusterGroup from 'react-leaflet-markercluster';
import 'leaflet/dist/leaflet.css';
import 'leaflet-extra-markers/dist/css/leaflet.extra-markers.min.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import L from 'leaflet';
import 'leaflet-extra-markers';

// Fullscreen control
import { FullscreenControl } from "react-leaflet-fullscreen";
import "react-leaflet-fullscreen/styles.css";

import { GoMoveToEnd } from "react-icons/go";
import { IoFilterSharp } from "react-icons/io5";

const getMarkerIcon = (status) => {
    let markerColor;

    const normalizedStatus = status.toLowerCase();

    switch (normalizedStatus) {
        case 'fail':
            markerColor = 'red';
            break;
        case 'active':
            markerColor = 'green';
            break;
        case 'init':
            markerColor = 'gray';
            break;
        case 'error':
            markerColor = 'orange';
            break;
        default:
            markerColor = 'orange';
    }

    return L.ExtraMarkers.icon({
        markerColor: markerColor,
        shape: 'circle',
        prefix: 'fa',
    });
};

const GatewaysMap = forwardRef(({ center, zoom = 13, markers }, ref) => {
    const [selectedStatuses, setSelectedStatuses] = useState(['fail', 'active', 'init', 'error']);
    const mapRef = useRef(null);

    useImperativeHandle(ref, () => ({
        zoomToCoordinates: (coords, zoomLevel) => {
            if (mapRef.current) {
                mapRef.current.setView(coords, zoomLevel);
            }
        },
    }));

    const memoizedMarkers = useMemo(() => {
        return markers
            .filter(
                (marker) =>
                    marker.latitude !== null &&
                    marker.longitude !== null &&
                    selectedStatuses.includes(marker.status)
            )
            .map((marker, index) => (
                <Marker
                    key={index}
                    position={[marker.latitude, marker.longitude]}
                    icon={getMarkerIcon(marker.status)}
                >
                    <Popup>
                        <div>
                            <h1>{marker.name}</h1>
                        </div>
                        <div>
                            <div onClick={() => window.open(`/gateways/${marker.id}/${marker.server}`, '_blank')}
                                className="flex flex-row gap-2 items-center justify-center border mt-2 cursor-pointer hover:bg-gray-300 active:scale-95">
                                Go to Gateway <GoMoveToEnd />
                            </div>
                        </div>
                    </Popup>
                </Marker>
            ));
    }, [markers, selectedStatuses]);

    return (
        <div style={{ position: 'relative', zIndex: 0 }}>
            <MapContainer
                center={center}
                zoom={zoom}
                ref={mapRef}
                attributionControl={false}
                style={{ height: '500px', width: '100%' }}
            >
                <div className='absolute top-[10px] left-[10px] bg-white p-2 rounded-md shadow-md z-[1000]'>
                    <div className='flex items-center justify-center'><IoFilterSharp /></div>
                    <p className='font-bold'>Filter by Status:</p>
                    <hr className='mt-2 mb-2' />
                    {['fail', 'active', 'init', 'error'].map((status) => (
                        <label key={status} className='mr-2 block text-start text-lg'>
                            <input
                                type="checkbox"
                                className='mr-2'
                                value={status}
                                checked={selectedStatuses.includes(status)}
                                onChange={() => setSelectedStatuses(prev =>
                                    prev.includes(status)
                                        ? prev.filter((s) => s !== status)
                                        : [...prev, status]
                                )}
                            />
                            {status.charAt(0).toUpperCase() + status.slice(1)}
                        </label>
                    ))}
                </div>
                <TileLayer
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
                <MarkerClusterGroup maxClusterRadius={25}>{memoizedMarkers}</MarkerClusterGroup>
                <FullscreenControl position='topright' />
            </MapContainer>
        </div>
    );
});

export default GatewaysMap;
